import { useEffect, useState } from "react";
import { Form, Col, Row } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import useDelete from "../../../global/hooks/useDelete";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";

import {
  CheckCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import ConfirmModal from "../../../global/components/ConfirmModal";
import { FaAddressCard } from "react-icons/fa6";
import { TERM_OPTIONS } from "../../../utils/Common";

const LICENSE_ITEM_OPTIONS = [
  { label: "Mobile Phone", value: 207 },
];

export const LimitedCardsForm = ({ type }) => {
  const [showModal, setShowModal] = useState(false);
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();

  const url = id
    ? `${process.env.REACT_APP_API_URL}/api/limited/cards/${id}/`
    : undefined;

  const postUrl = `${process.env.REACT_APP_API_URL}/api/limited/cards/`;
  const putUrl = `${process.env.REACT_APP_API_URL}/api/limited/cards/${id}/`;
  const deleteUrl = id
    ? `${process.env.REACT_APP_API_URL}/api/limited/cards/${id}/`
    : undefined;
  const { data, loading, error, refetch } = useGet(url);

  const {
    data: postData,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);
  const { data: putData, error: putError, putData: putAction } = usePut(putUrl);
  const {
    data: deletedData,
    error: deletedDataError,
    deleteData,
  } = useDelete(deleteUrl);

  const onFinish = async (values) => {
    try {
      const modifiedValue = {
        ...values,
        LicenseItem: Number(values.LicenseItem),
        LicenseCode: Number(values.LicenseCode),
        Term: parseInt(values.Term, 10),
        Price: values.Price
          ? Number(parseFloat(values.Price).toFixed(2))
          : values.Price,
      };
      if (id) {
        await putAction(modifiedValue);
      } else {
        await postAction(modifiedValue);
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
      console.log(error);
    }
  };

  const onReset = () => {
    navigate(-1);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteData();
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
  };

  useEffect(() => {
    const getLimitedCardInfoById = () => {
      try {
        form.resetFields();
        if (data?.data) {
          form.setFieldsValue({
            LimitedCardName: data?.data?.LimitedCardName || "",
            LimitedCardDesc: data?.data?.LimitedCardDesc || "",
            LicenseItem:
              data?.data?.LicenseItem != null
                ? Number(data.data.LicenseItem)
                : 207,
            LicenseCode:
              data?.data?.LicenseCode != null
                ? Number(data.data.LicenseCode)
                : undefined,
            Term: data?.data?.Term,
            Price: data?.data?.Price ?? "",
          });
        } else if (id) {
          console.warn("No data found for the given ID");
        } else {
          form.setFieldsValue({
            LicenseItem: 207,
          });
        }
      } catch (error) {
        console.log({ error });
        openErrorNotification("Error fetching data!");
      }
    };

    getLimitedCardInfoById();
  }, [id, data, form, type]);

  useEffect(() => {
    if (Array.isArray(postError?.response?.data?.errors?.non_field_errors)) {
      postError?.response?.data?.errors?.non_field_errors?.map((item) =>
        openErrorNotification(item),
      );
      return;
    }
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postError && postData) {
      openConfirmNotification("Success");
      setTimeout(() => {
        navigate(-1);
      }, 200);
    }
  }, [postError, postData]);

  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    } else if (!putError && putData) {
      openConfirmNotification("Success");
      refetch();
      navigate("/limited-license");
    }
  }, [putData, putError]);

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      navigate("/limited-license");
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    } else {
      return;
    }
  }, [deletedData, deletedDataError]);

  const breadcrumbItems = [
    {
      label: "Limited License",
      href: "/limited-license",
    },
    {
      label: id ? (type === "info" ? "Information" : "Edit") : "Add",
      href: id
        ? type === "info"
          ? `/limited-license/info/${id}`
          : `/limited-license/edit/${id}`
        : "/limited-license/add/",
    },
  ];

  const buttonsData =
    type === "info"
      ? [
          {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => {
              navigate(`/limited-license/edit/${id}`);
            },
          },
          {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: () => {
              setShowModal(true);
            },
          },
          {
            label: "List",
            icon: <UnorderedListOutlined />,
            onClick: () => {
              navigate(-1);
            },
          },
        ]
      : type === "edit"
        ? [
            {
              label: "Apply",
              icon: <CheckCircleOutlined />,
              onClick: () => form.submit(),
            },
            {
              label: "Cancel",
              icon: <CloseOutlined />,
              onClick: onReset,
            },
          ]
        : [
            {
              label: "Apply",
              icon: <CheckCircleOutlined />,
              onClick: () => form.submit(),
            },
            {
              label: "Cancel",
              icon: <CloseOutlined />,
              onClick: onReset,
            },
          ];

  return (
    <div>
      <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
          <CommonButton buttonsData={buttonsData} />
        </CommonBreadcrumb>
      </div>

      <div className="">
        <Form
          validateTrigger={"onChange"}
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          layout={"vertical"}
          autoComplete={"off"}
          initialValues={{ LicenseItem: 207 }}
        >
          <div className="border bg-white rounded-[4px] overflow-hidden">
            <div className="flex items-center bg-gray-200 p-2.5">
              <FaAddressCard className="text-[18px] text-black " />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                Limited License
              </span>
            </div>
            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Name"
                    name="LimitedCardName"
                    disabled={type === "info"}
                    autoComplete="off"
                    required
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Description"
                    name="LimitedCardDesc"
                    disabled={type === "info"}
                    autoComplete="off"
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomSelect
                    label="License Item"
                    name="LicenseItem"
                    options={LICENSE_ITEM_OPTIONS}
                    placeholder="Please select"
                    disabled={type === "info"}
                    required
                    loading={loading}
                    allowClear={false}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="License Code"
                    name="LicenseCode"
                    disabled={type === "info"}
                    type="number"
                    required
                    loading={loading}
                    rules={[
                      {
                        required: true,
                        message: "License Code is required",
                      },
                      {
                        validator: (_, value) => {
                          if (value == null || value === "") {
                            return Promise.resolve();
                          }
                          const n = Number(value);
                          if (!Number.isFinite(n) || n < 0) {
                            return Promise.reject(
                              new Error("License Code must be a valid number"),
                            );
                          }
                          if (n > 65535) {
                            return Promise.reject(
                              new Error(
                                "License Code must be less than or equal to 65535",
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomSelect
                    label="Term"
                    name="Term"
                    options={TERM_OPTIONS}
                    placeholder="Please select"
                    disabled={type === "info"}
                    required
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Price"
                    name="Price"
                    disabled={type === "info"}
                    type="number"
                    required
                    prefix={"$"}
                    style={{ height: "30px" }}
                    loading={loading}
                  />
                </Col>
              </Row>
            </div>
          </div>
        </Form>
      </div>
      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onDeleteConfirm}
      />
    </div>
  );
};
